import React, { useEffect, useState } from "react";
import "./createProject.scss";
import Header from "../../../layout/header/Header";
import { useNavigate, useLocation } from "react-router-dom";
import { convertToIso } from "../../../../database";
import { toast } from "react-toastify";
import axiosInstance from "../../../../httpInterceptor";
import { IoArrowBackSharp } from "react-icons/io5";
import ProjectReadinessForm from "../ProjectReadinessForm/ProjectReadinessForm";

const CASE_STUDY_BASE_URL = process.env.REACT_APP_CASE_STUDY_SERVICE_BASE_URL;

const CreateProject = () => {
  //react router dom useNavigate hook for navigation purpose
  const navigate = useNavigate();
  const location = useLocation();

  //local states for the create project component
  const [isData, setIsData] = useState(false);
  const [showProjectReadiness, setShowProjectReadiness] = useState(false);
  const [compassData, setCompassData] = useState<any>(null);
  const [filteredCompassData, setFilteredCompassData] = useState<any>(null);
  const [reviewer, setReviewerData] = useState<any>(null);
  const [reviewerNames, setReviewerNames] = useState<any>(null);
  const [isInvalidReviewer, setIsInvalidReviewer] = useState(false);
  const [hasBlurred, setHasBlurred] = useState(false);
  const [questionsLoading, setQuestionsLoading] = useState(false);

  type Question = {
    _id?: number;
    description: string;
    set: string;
    category: string;
    type: "text" | "dropdown" | "radio" | "checkbox" | null;
    values?: { option: string; response: string }[];
  };
  const [questions, setQuestions] = useState<Question[]>([]);
  const [billingProjectIds, setBillingProjectIds] = useState([]);

  // NEW: capability area / capability dropdown data & loading flags
  const [capabilityAreas, setCapabilityAreas] = useState<string[]>([]);
  const [capabilities, setCapabilities] = useState<string[]>([]);
  const [capabilityAreasLoading, setCapabilityAreasLoading] = useState(false);
  const [capabilitiesLoading, setCapabilitiesLoading] = useState(false);
  const [selectedCapabilityAreas, setSelectedCapabilityAreas] = useState<string[]>([]);
  const [selectedCapabilities, setSelectedCapabilities] = useState<string[]>([]);

  // Check if we should show Project Readiness form based on navigation state
  useEffect(() => {
    if (location.state?.showProjectReadiness) {
      setShowProjectReadiness(true);
    }
  }, [location.state]);

  const [projectData, setProjectData] = useState({
    proj_id: "",
    set_name: "",
    proj_name: "",
    type: "",
    pmid: "",
    pmname: "",
    du: "",
    pu: "",
    account: "",
    billing_project_id: "",
    proj_start_date: "",
    proj_end_date: "",
    proj_desc: "",
    reviewer: "",
    capability_area: [] as string[],  // Changed to array
    capability: [] as string[],        // Changed to array
    mapped_industry: "",
    created_by: `${sessionStorage.getItem(
      "PBT_FIRSTNAME"
    )} ${sessionStorage.getItem("PBT_LASTNAME")}`,
  });
  const isFormValid = () => {
    const requiredFields: (keyof typeof projectData)[] = [
      "proj_id",
      "set_name",
      "proj_name",
      "type",
      "pmid",
      "pmname",
      "du",
      "pu",
      "account",
      "billing_project_id",
      "proj_start_date",
      "proj_end_date",
      "proj_desc",
      "reviewer",
    ];

    const basicFieldsValid = requiredFields.every((field) => projectData[field]?.toString().trim() !== "");
    // Removed capability area validation - no longer mandatory
    
    return basicFieldsValid;
  };

  //handling the submit event of the form
  const handleCreateProjectFormSubmit = async (
    e: React.FormEvent<HTMLFormElement>
  ) => {
    e.preventDefault();
    if (!reviewerNames || !reviewerNames.includes(projectData.reviewer)) {
      toast.error("Please select PBT Assessor from suggestions!");
      return;
    }

    try {
      // Check if the specific combination of proj_id and set_name already exists
      try {
        const checkRes = await axiosInstance.get(
          `${process.env.REACT_APP_DASHBOARD_SERVICE_BASE_URL}/project/${projectData.proj_id}/${projectData.set_name}`
        );
        
        // If project with this proj_id and set_name exists, throw an error
        if (checkRes.data && checkRes.data._id) {
          toast.error("Assessment for this Project ID already exists in the same set!");
          return;
        }
      } catch (checkError: any) {
        // If 404, project doesn't exist with this combination - proceed to create
        if (checkError.response && checkError.response.status !== 404) {
          // If it's not a 404 error, throw it
          throw checkError;
        }
      }

      // Create new project
      const res = await axiosInstance.post(
        `${process.env.REACT_APP_DASHBOARD_SERVICE_BASE_URL}/add-project`,
        projectData
      );
      if (res.data.message) {
        setProjectData({
          proj_id: "",
          set_name: "",
          proj_name: "",
          type: "",
          pmid: "",
          pmname: "",
          du: "",
          pu: "",
          account: "",
          billing_project_id: "",
          proj_start_date: "",
          proj_end_date: "",
          proj_desc: "",
          reviewer: "",
          capability_area: [],
          capability: [],
          mapped_industry: "",
          created_by: `${sessionStorage.getItem(
            "PBT_FIRSTNAME"
          )} ${sessionStorage.getItem("PBT_LASTNAME")}`,
        });
        console.log(res.data.message);
        return toast.error(res.data.message);
      } else {
        // Fetch data after successful project creation
        await axiosInstance.post(
          `${process.env.REACT_APP_DASHBOARD_SERVICE_BASE_URL}/lookup`,
          { du: projectData.du, account: projectData.account }
        );

        toast.success("Assessment created successfully");
        localStorage.removeItem(`responses_${projectData.proj_id}`);

        setTimeout(() => {
          navigate(
            `/dashboard/new-assessment/${projectData.proj_id}/${projectData.set_name}`
          );
        }, 2500);
      }
    } catch (error) {
      console.log(error);
      toast.error("Failed to create assessment");
    }
  };



  const fetchUserDataForReviewer = async (query: string) => {
    if (!query) {
      setReviewerData(null);
      setIsInvalidReviewer(true);
      return;
    }
    try {
      const res = await axiosInstance.get(
        `${process.env.REACT_APP_USER_SERVICE_BASE_URL}/getPSNumber/?search=${query}`
      );

      const result = res.data.filter((item: any) => {
        if (!query) {
          setReviewerData(null);
          setIsInvalidReviewer(true);
        }
        {
          const searchText = query.toLowerCase();
          const fullName = item.name.toLowerCase();

          return query && fullName.startsWith(searchText);
        }
      });
      setReviewerData(result);
      setIsInvalidReviewer(result.length === 0);
      const names = result.map((item: any) => {
        const { name, ...rest } = item;
        return name;
      });
      setReviewerNames(names);
    } catch (error) {
      console.log(error);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = e.target;
    setProjectData({ ...projectData, [name]: value });
  };

  const handleReviewerChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = e.target;
    setProjectData({ ...projectData, [name]: value });
    setIsInvalidReviewer(false);

    if (value.trim() === "") {
      setReviewerData([]);
      setIsInvalidReviewer(false); // Show message when input is empty
    } else {
      fetchUserDataForReviewer(value);
    }
  };
  const handleChange1 = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = e.target;
    setProjectData({ ...projectData, [name]: value });
    // fetchUserDataForReviewer(value);
  };

  const handleReviewerOnClick = (value: string) => {
    setProjectData((prevData: any) => ({
      ...prevData,
      reviewer: value,
    }));
    setReviewerData(null);
    setIsInvalidReviewer(false);
    setHasBlurred(false);
  };
  useEffect(() => {
    console.log("Updated Billing Project IDs:", billingProjectIds);
  }, [billingProjectIds]);

  const extractList = (payload: any): string[] => {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.result)) return payload.result;
    if (Array.isArray(payload?.data)) return payload.data;
    const values = Object.values(payload ?? {});
    if (values.length > 0 && Array.isArray(values[0])) return values[0] as string[];
    return [];
  };

  const fetchCapabilitiesForAreas = async (areas: string[]) => {
    if (!areas || areas.length === 0) {
      setCapabilities([]);
      setCapabilitiesLoading(false);
      return;
    }

    if (areas.includes("N/A")) {
      setCapabilities(["N/A"]);
      setCapabilitiesLoading(false);
      return;
    }

    setCapabilitiesLoading(true);
    try {
      const response = await axiosInstance.post(
        `${CASE_STUDY_BASE_URL}/casestudy/capability`,
        { capabilityAreas: areas },
        { headers: { "X-API-KEY": "PBTteamkey" } }
      );
      const caps = Array.from(new Set(extractList(response.data)));
      setCapabilities(caps);
    } catch (error) {
      console.error("Error fetching capabilities:", error);
      setCapabilities([]);
    } finally {
      setCapabilitiesLoading(false);
    }
  };

  const fetchCapabilityAreasByPU = async (puValue?: string | null) => {
    const normalized = (puValue ?? "").trim().toUpperCase();
    const search =
      normalized === "DL"
        ? "Digital Engineering"
        : normalized === "TT"
        ? "Quality Engineering"
        : null;

    if (!search) {
      setCapabilityAreas(["N/A"]);
      setCapabilities([]);
      setProjectData(prev => ({ ...prev, capability_area: [], capability: [] }));
      setCapabilityAreasLoading(false);
      setCapabilitiesLoading(false);
      return;
    }

    setCapabilityAreasLoading(true);
    setCapabilities([]);
    setProjectData(prev => ({ ...prev, capability_area: [], capability: [] }));
    try {
      const response = await axiosInstance.post(
        `${CASE_STUDY_BASE_URL}/casestudy/capabilityarea`,
        { search },
        { headers: { "X-API-KEY": "PBTteamkey" } }
      );
      const areas = extractList(response.data);
      setCapabilityAreas(areas);
    } catch (error) {
      console.error("Error fetching capability areas:", error);
      setCapabilityAreas([]);
      setCapabilities([]);
    } finally {
      setCapabilityAreasLoading(false);
    }
  };

  const handleProjectIdBlur = async (value: string) => {
    try {
      const { data } = await axiosInstance.get(
        `${process.env.REACT_APP_COMPASS_BOOMI_URL}?Projectid=${value}`,
        {
          headers: {
            "Content-Type": "application/json",
            "x-api-key": `${process.env.REACT_APP_COMPASS_X_API_KEY}`,
          },
        }
      );
      const result = data.result[0];
      setBillingProjectIds(result.billing_project_id);

      if (projectData.proj_id.length > 3) {
        if (data.result.length === 0) {
          alert("Please enter a valid project id");
          setProjectData((prevData) => ({
            ...prevData,
            proj_id: "",
            capability_area: ["N/A"],
            capability: ["N/A"],
          }));
          setCapabilityAreas(["N/A"]);
          setCapabilities(["N/A"]);
          setCapabilityAreasLoading(false);
          setCapabilitiesLoading(false);
        } else {
          if (result.projectNumber) {
            setIsData(true);
          }
          
          const descRes = await axiosInstance.get(
            `${process.env.REACT_APP_DASHBOARD_SERVICE_BASE_URL}/project/${projectData.proj_id}`
          );
          console.log(descRes);
          const existingDesc = descRes.data.project && typeof descRes.data.project === "object"
            ? descRes.data.project.proj_desc || ""
            : "";

          console.log(existingDesc);

          setProjectData((prevData) => ({
            ...prevData,
            proj_name: result.projectName,
            type: result.u_project_category,
            pmid: result.primaryPM_psno,
            pmname: result.primaryPM,
            du: result.u_delivery_unit,
            pu: result.u_practice_unit,
            account: result.accountName,
            mapped_industry: getMappedIndustry(result.u_delivery_unit),
            billing_project_id:
              Array.isArray(result.billing_project_id) &&
              result.billing_project_id.length !== 0
                ? ""
                : result.billing_project_id,
            proj_start_date: result.start_date,
            proj_end_date: result.end_date,
            proj_desc: existingDesc,
            created_by: `${sessionStorage.getItem(
              "PBT_FIRSTNAME"
            )} ${sessionStorage.getItem("PBT_LASTNAME")}`,
          }));
          const billingIds = Array.isArray(result.billing_project_id)
            ? result.billing_project_id
            : [];
          setBillingProjectIds(billingIds);
          await fetchCapabilityAreasByPU(result.u_practice_unit);
          }
        }
    } catch (error) {
      console.error("Error fetching data", error);
    }
  };
  const handleReviewerBlur = () => {
    setTimeout(() => {
      setReviewerData(null);
      setIsInvalidReviewer(false);
      setHasBlurred(true);
    }, 300);
  };
  useEffect(() => {
    const fetchQuestions = async () => {
      setQuestionsLoading(true);
      try {
        const response = await axiosInstance.get(
          `${process.env.REACT_APP_QUESTION_SERVICE_BASE_URL}/questions`
        );
        console.log("Questions API Response:", response.data);
        setQuestions(response.data);
      } catch (error) {
        console.error("Error fetching questions:", error);
        toast.error("Error fetching questions");
      } finally {
        setQuestionsLoading(false);
      }
    };
    fetchQuestions();
  }, []);

  const groupQuestionsBySet = (questions: any) => {
    return questions.reduce((acc: any, question: any) => {
      const set = question.set || question.set_name; // Handle both 'set' and 'set_name' properties
      if (set && !acc[set]) acc[set] = [];
      if (set) acc[set].push(question);
      return acc;
    }, {});
  };
  const groupedQuestionsBySet = groupQuestionsBySet(questions);
  const suggestions = Object.keys(groupedQuestionsBySet);
  
  console.log("Questions:", questions);
  console.log("Grouped Questions:", groupedQuestionsBySet);
  console.log("Suggestions:", suggestions);

  const redirectToDashboard = () => {
    navigate("/dashboard");
  };

  // REPLACE old handleCapabilitySelect with two handlers
  const handleCapabilityAreaChange = (
    _: any,
    newValue: string[]
  ) => {
    setProjectData(prev => ({
      ...prev,
      capability_area: newValue,
      capability: []
    }));
    setCapabilities([]);
    void fetchCapabilitiesForAreas(newValue); // Already correct - calling the plural version
  };

  const handleCapabilityChange = (
    _: any,
    newValue: string[]
  ) => {
    setProjectData(prev => ({ ...prev, capability: newValue }));
  };

  // Custom multi-select handlers for dropdown-style components
  const handleCapabilityAreaSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    if (value && !projectData.capability_area.includes(value)) {
      const newValues = [...projectData.capability_area, value];
      setProjectData(prev => ({
        ...prev,
        capability_area: newValues,
        capability: []
      }));
      setCapabilities([]);
      void fetchCapabilitiesForAreas(newValues);
    }
  };

  const handleCapabilitySelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    if (value && !projectData.capability.includes(value)) {
      const newValues = [...projectData.capability, value];
      setProjectData(prev => ({ ...prev, capability: newValues }));
    }
  };

  const removeCapabilityArea = (valueToRemove: string) => {
    const newValues = projectData.capability_area.filter(item => item !== valueToRemove);
    setProjectData(prev => ({
      ...prev,
      capability_area: newValues,
      capability: []
    }));
    setCapabilities([]);
    void fetchCapabilitiesForAreas(newValues);
  };

  const removeCapability = (valueToRemove: string) => {
    const newValues = projectData.capability.filter(item => item !== valueToRemove);
    setProjectData(prev => ({ ...prev, capability: newValues }));
  };

  return (
    <div className="createProjectContainer">
      <Header fromProjectReadiness={showProjectReadiness} />
      
      {/* Toggle Switch */}
      <div style={{ 
        display: "flex", 
        alignItems: "center", 
        justifyContent: "center",
        gap: "10px",
        backgroundColor: "#f0f4f8",
        padding: "6px 12px",
        borderRadius: "20px",
        border: "1px solid #d1d5db",
        margin: "15px auto",
        width: "fit-content"
      }}>
        <span style={{ 
          fontSize: "0.85rem", 
          fontWeight: showProjectReadiness ? "normal" : "600",
          color: showProjectReadiness ? "#6b7280" : "#074173"
        }}>
          New Assessment
        </span>
        <label style={{ 
          position: "relative", 
          display: "inline-block", 
          width: "48px", 
          height: "24px",
          cursor: "pointer"
        }}>
          <input
            type="checkbox"
            checked={showProjectReadiness}
            onChange={() => setShowProjectReadiness(!showProjectReadiness)}
            style={{ opacity: 0, width: 0, height: 0 }}
          />
          <span style={{
            position: "absolute",
            cursor: "pointer",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: showProjectReadiness ? "#0d5eff" : "#cbd5e1",
            transition: "0.4s",
            borderRadius: "24px",
          }}>
            <span style={{
              position: "absolute",
              content: '""',
              height: "18px",
              width: "18px",
              left: showProjectReadiness ? "26px" : "3px",
              bottom: "3px",
              backgroundColor: "white",
              transition: "0.4s",
              borderRadius: "50%",
            }}></span>
          </span>
        </label>
        <span style={{ 
          fontSize: "0.85rem", 
          fontWeight: showProjectReadiness ? "600" : "normal",
          color: showProjectReadiness ? "#074173" : "#6b7280"
        }}>
          Readiness Assessment
        </span>
      </div>

      {showProjectReadiness ? (
        <ProjectReadinessForm embedded={true} />
      ) : (
      <>
      <form
        onSubmit={handleCreateProjectFormSubmit}
        className="createProjectForm"
      >
        <div className="createProjectFormBox">
          <div className="createProjectFormRow">
            <div className="row projectIdInputBoxWithSuggestions project-id-row">
              <label>
                Project ID (PRJ ID)
                <span style={{ color: "red" }}>*</span>
              </label>
              <input
                type="text"
                name="proj_id"
                autoComplete="off"
                onBlur={(e) => handleProjectIdBlur(e.target.value)}
                required
                value={projectData.proj_id}
                onChange={handleChange1}
              />
            </div>
            <div className="row">
              <label>Project Name</label>
              <input
                type="text"
                name="proj_name"
                autoComplete="off"
                required
                readOnly
                disabled
                value={projectData.proj_name}
                onChange={handleChange}
              />
            </div>
            <div className="row">
              <label>Project Category</label>
              <input
                type="text"
                name="type"
                autoComplete="off"
                required
                readOnly
                disabled
                value={projectData.type}
                onChange={handleChange}
              />
            </div>
          </div>
          <div className="createProjectFormRow">
            <div className="row">
              <label>PM PS ID</label>
              <input
                type="text"
                autoComplete="off"
                name="pmid"
                required
                readOnly
                disabled
                value={projectData.pmid}
                onChange={handleChange}
              />
            </div>
            <div className="row">
              <label>PM Name</label>
              <input
                type="text"
                autoComplete="off"
                name="pmname"
                required
                readOnly
                disabled
                value={projectData.pmname}
                onChange={handleChange}
              />
            </div>
            <div className="row">
              <label>Account</label>
              <input
                type="text"
                name="account"
                autoComplete="off"
                required
                readOnly
                disabled
                value={projectData.account}
                onChange={handleChange}
              />
            </div>
          </div>
          <div className="createProjectFormRow">
            <div className="row">
              <label>Delivery Unit (DU)</label>
              <input
                type="text"
                name="du"
                autoComplete="off"
                required
                disabled
                readOnly
                value={projectData.du}
                onChange={handleChange}
              />
            </div>
            <div className="row">
              <label>Mapped Industry</label>
              <input
                type="text"
                name="mapped_industry"
                autoComplete="off"
                value={projectData.mapped_industry}
                onChange={handleChange1}
                readOnly={!isData}
                disabled={!isData}
              />
            </div>
            <div className="row">
              <label>Practice Unit (PU)</label>
              <input
                type="text"
                name="pu"
                autoComplete="off"
                required
                disabled
                readOnly
                value={projectData.pu}
                onChange={handleChange}
              />
            </div>
          </div>
          <div className="createProjectFormRow">
            <div className="row">
              <label>
                Capability Area
                {/* Removed the red asterisk to indicate it's no longer mandatory */}
              </label>
              <div className="multi-select-container">
                <select
                  value=""
                  onChange={handleCapabilityAreaSelect}
                  disabled={capabilityAreasLoading || capabilityAreas.length === 0}
                  className="enhanced-dropdown"
                >
                  <option value="">Select Capability Area</option>
                  {capabilityAreas
                    .filter(area => !projectData.capability_area.includes(area))
                    .map((area) => (
                      <option key={area} value={area} className="enhanced-option">
                        {area}
                      </option>
                    ))}
                </select>
                {projectData.capability_area.length > 0 && (
                  <div className="selected-items">
                    {projectData.capability_area.map((area) => (
                      <span key={area} className="selected-item">
                        {area}
                        <button
                          type="button"
                          onClick={() => removeCapabilityArea(area)}
                          className="remove-item"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="row">
              <label>
                Capability
              </label>
              <div className="multi-select-container">
                <select
                  value=""
                  onChange={handleCapabilitySelect}
                  disabled={
                    capabilitiesLoading ||
                    !projectData.capability_area ||
                    projectData.capability_area.length === 0 ||
                    capabilities.length === 0
                  }
                  className="enhanced-dropdown"
                >
                  <option value="">Select Capability</option>
                  {capabilities
                    .filter(capability => !projectData.capability.includes(capability))
                    .map((capability) => (
                      <option key={capability} value={capability} className="enhanced-option">
                        {capability}
                      </option>
                    ))}
                </select>
                {projectData.capability.length > 0 && (
                  <div className="selected-items">
                    {projectData.capability.map((capability) => (
                      <span key={capability} className="selected-item">
                        {capability}
                        <button
                          type="button"
                          onClick={() => removeCapability(capability)}
                          className="remove-item"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="row assessment-type-row">
              <label>
                Assessment Type
                <span style={{ color: "red" }}>*</span>
              </label>
              <select
                name="set_name"
                value={projectData.set_name}
                required
                onChange={handleChange}
                className="enhanced-dropdown"
                disabled={questionsLoading}
              >
                <option value="">
                  {questionsLoading ? "Loading assessment types..." : "Select Assessment Type"}
                </option>
                {suggestions.length > 0 ? (
                  suggestions.map((set) => (
                    <option key={set} value={set} className="enhanced-option">
                      {set}
                    </option>
                  ))
                ) : !questionsLoading && questions.length > 0 ? (
                  <option disabled>No assessment types found</option>
                ) : null}
              </select>
            </div>
          </div>
          <div className="createProjectFormRow">
            <div className="row">
              <label>Project Start Date (MM-DD-YYYY)</label>
              <input
                type="text"
                name="proj_start_date"
                autoComplete="off"
                required
                disabled
                readOnly
                value={
                  projectData.proj_start_date
                    ? convertToIso(projectData.proj_start_date)
                    : ""
                }
                onChange={handleChange}
              />
            </div>
            <div className="row">
              <label>Project End Date (MM-DD-YYYY)</label>
              <input
                type="text"
                value={
                  projectData.proj_end_date
                    ? convertToIso(projectData.proj_end_date)
                    : ""
                }
                name="proj_end_date"
                readOnly
                disabled
                onChange={handleChange}
              />
            </div>
            <div className="row billing-project-row">
              <label>Billing Project ID</label>
              <input
                type="text"
                name="billing_project_id"
                autoComplete="off"
                required
                readOnly={!isData}
                disabled={!isData}
                value={projectData.billing_project_id}
                onChange={handleChange}
              />
              {Array.isArray(billingProjectIds) && billingProjectIds.length > 1 && (
                <select
                  name="billing_project_id"
                  required
                  value={projectData.billing_project_id}
                  onChange={handleChange}
                >
                  <option value="">Select Billing Project ID</option>
                  {billingProjectIds.map((id) => (
                    <option key={id} value={id}>
                      {id}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>
          <div className="createProjectFormRow">
            <div className="row projectIdInputBoxWithSuggestions pbt-assessor-row">
              <label>
                Assessor (By Name)
                <span style={{ color: "red" }}>*</span>
              </label>
              <input
                type="text"
                autoComplete="off"
                name="reviewer"
                onFocus={(e) => fetchUserDataForReviewer(e.target.value)}
                onBlur={handleReviewerBlur}
                required
                value={projectData.reviewer}
                readOnly={!isData}
                disabled={!isData}
                onChange={handleReviewerChange}
              />
              <ul className={reviewer?.length > 0 ? "showProjResults" : ""}>
                {reviewer?.length > 0 &&
                  reviewer.map((result: any, idx: number) => (
                    <li
                      className="resultItems"
                      key={idx}
                      onClick={() => handleReviewerOnClick(result.name)}
                    >
                      <span>{result.name}</span>
                    </li>
                  ))}
              </ul>
            </div>
            <div className="row full-width">
              <label>
                Project Description
                <span style={{ color: "red" }}>*</span>
              </label>
              <textarea
                name="proj_desc"
                // allow editing when project details have been loaded
                readOnly={!isData}
                disabled={!isData}
                value={projectData.proj_desc}
                required
                onChange={handleChange1}
                style={{
                  width: "100%",
                  minHeight: "42px",
                  padding: "8px",
                  resize: "vertical"
                }}
              />
            </div>
          </div>
          {/* Removed old rows that previously contained PBT Assessor / Project Description */}
        </div>

        <div className="createProjectBtn">
          <button
            type="submit"
            disabled={!isFormValid()}
            style={{ cursor: !isFormValid() ? "not-allowed" : "pointer" }}
          >
            Submit
          </button>
        </div>
      </form>
      <div className="backToDashboard">
        {/* <button onClick={()=>redirectToDashboard()}> <IoArrowBackSharp style={{translate:'-10px',fontSize:'1.1rem'}}/>Back</button> */}

        <button onClick={() => redirectToDashboard()}>
          <IoArrowBackSharp
            style={{ translate: "-10px", fontSize: "1.2rem" }}
          />
          <span style={{ marginLeft: "5px" }}>Back</span>
        </button>
      </div>
      </>
      )}
    </div>
  );
};

export default CreateProject;

const DU_TO_MAPPED_INDUSTRY: Record<string, string> = {
  BB: "BANKING & FINANCIAL SERVICES",
  CSU: "BANKING & FINANCIAL SERVICES",
  DSA: "BANKING & FINANCIAL SERVICES",
  BC: "BANKING & FINANCIAL SERVICES",
  ND: "BANKING & FINANCIAL SERVICES",
  BF: "BANKING & FINANCIAL SERVICES",
  EG: "ENERGY",
  GP: "ENERGY",
  EY: "ENERGY",
  HA: "HEALTH",
  HE: "HEALTH",
  HT: "HI-TECH",
  HH: "HI-TECH",
  NN: "INSURANCE",
  DL: "INSURANCE",
  NC: "INSURANCE",
  NS: "INSURANCE",
  PN: "INSURANCE",
  LS: "LIFE SCIENCES",
  MM: "MANUFACTURING",
  MN: "MANUFACTURING",
  ME: "MEDIA & ENTERTAINMENT",
  MD: "MEDIA & ENTERTAINMENT",
  FT: "MICROSOFT",
  RR: "RETAIL & CONSUMER PACKAGED GOODS",
  RE: "RETAIL & CONSUMER PACKAGED GOODS",
  TW: "TRAVEL TRANSPORT & HOSPITALITY",
  DATA: "TRAVEL TRANSPORT & HOSPITALITY",
  UL: "UTILITIES",
  UA: "UTILITIES",
};
const getMappedIndustry = (du: string | undefined | null) =>
  du ? DU_TO_MAPPED_INDUSTRY[du.toUpperCase()] ?? "N/A" : "N/A";
